import CanvasLayer from './CanvasLayer';
import SymbolFactory from '../symbol/SymbolFactory';
import GeometryTransform from '../geometry/GeometryTransform';
import GeometryFactory from '../geometry/GeometryFactory';

/**
 * 在Canvas上绘制Symbol的图层基类
 * @interface
 */
export default class SymbolLayer extends CanvasLayer {
    /**
     * 构造函数
     * @param {string} id 图层ID
     * @param {Object} options 可选项
     */
    constructor(id, options) {
        super(id, options);

        /**
         * 默认symbol配置
         * @type {object}
         */
        this._defaultSymbol = this.options.symbol;

        this._geometryFactory = GeometryFactory.getInstance();
        this._symbolFactory = SymbolFactory.getInstance();

        this._resourceLoaded = false;
    }

    /**
     * 绘制图层
     */
    _draw() {
        const symbols = this.getSymbols();

        if (this._resourceLoaded) {
            this._drawSymbols(symbols);
        } else {
            this._loadDependencyResource(symbols).then(() => {
                this._clear();

                this._resourceLoaded = true;
    
                this._drawSymbols(symbols);
            });
        }
    }

    /**
     * 获取要绘制的图形
     * @returns {Array} 图形数组
     */
    getSymbols() {
        return [];
    }

    /**
     * 绘制图标
     * @param {object} symbol 图标对象
     * @param {object} tile 瓦片对象
     */
    _drawSymbols(symbols, tile) {
        if (!this._ctx) {
            return;
        }
        
        for (const symbol of symbols) {
            // 这里转换的是geometry对象，不是geojson，因此必须要用GeometryTransform
            let geometry = {}
            if (symbol.transformGeo) {
                geometry = this._transformGeo(symbol, tile)
            } else {
                geometry = GeometryTransform.convert(symbol.geometry, this._lngLatToContainerPoint);
            }

            symbol.draw(this._ctx, geometry);
        }
    }

    /**
     * 加载依赖的图片资源
     * @returns {Promise} promise
     */
    async _loadDependencyResource(symbols) {
        const urls = this._getDependencyResource(symbols);
        const loader = this._map.ImageLoader;

        return loader.load(urls);
    }

    /**
     * 获取依赖的图片资源的url
     * @param {Array} drawItems 要绘制的对象列表
     * @returns {Array} 图片资源的url数组
     */
    _getDependencyResource(symbols) {
        const urls = [];
        for (const symbol of symbols) {
            urls.push(...symbol.getImgUrls());
        }

        return urls;
    }

    /**
     * 根据符号定义json，生产符号对象
     * @param {object<json>} symbol 符号定义
     */
    createSymbol(symbol) {
        return this._symbolFactory.createSymbol(symbol);
    }

    /**
     * 根据geojson生成geometry对象
     * @param {object} geojson 几何坐标
     */
    createGeometry(geojson) {
        return this._geometryFactory.fromGeojson(geojson);
    }

    /**
     * 将经纬度坐标转换为屏幕内像素坐标，用于渲染绘制
     * 在坐标转换类中使用，为了保证this对象正确，因此使用箭头函数来定义
     * @param {number} lng 经度坐标
     * @param {number} lat 维度坐标
     * @returns {object<L.Point>} 像素坐标对象
     */
    _lngLatToContainerPoint = (lng, lat) => this._map.latLngToContainerPoint([lat, lng]);
    
    /**
     * 将瓦片度坐标转换为屏幕内像素坐标，用于渲染绘制
     * @param {object} symbol 图标对象
     * @param {object} tile 瓦片对象
     */
    _transformGeo(symbol, tile) {
        const convertFunc = this._map.GeojsonConverter.getTilePointToLngLat(tile);
        const geometry = this._map.GeojsonConverter.convert(symbol.geometry, convertFunc);
        symbol.geometry = this.createGeometry(geometry);
        symbol.transformGeo = null;
        return GeometryTransform.convert(symbol.geometry, this._lngLatToContainerPoint);
    }
}
